<template>
    <fade-up-animation :element="() => $refs.section">
        <section class="recipe-header" ref="section">
            <div class="header-container">
                <!-- mobile image -->
                <div class="image-holder">
                    <lazy-image
                        :src="mobileImage.src ? mobileImage.src : desktopImage.src"
                        :alt="mobileImage.alt ? mobileImage.alt : desktopImage.alt"
                        :width="mobileImage.src ? mobileImage.width : desktopImage.width"
                        :height="mobileImage.src ? mobileImage.height : desktopImage.height"
                        class="mobile-recipe-image"
                        v-test:mobileImage
                    />
                </div>

                <b-container>
                    <div class="header-content-container">
                        <div class="header">
                            <h1
                                class="name"
                                v-test:name
                            >
                                {{ name }}
                            </h1>
                            <share-links
                                :title="name"
                                :media="desktopImage.src"
                            />
                        </div>

                        <b-row>
                            <b-col lg="6">
                                <recipe-details
                                    :serves="recipeDetails.serves"
                                    :prep-time="recipeDetails.prepTime"
                                    :cook-time="recipeDetails.cookTime"
                                />

                                <b-button
                                    variant="primary"
                                    class="start-cooking-btn"
                                    href="#recipe-details-section"
                                    @click.prevent="scrollToInstructions"
                                    v-test:startCookingBtn
                                >
                                    {{ $snippet('start_cooking', 'start cooking') }}
                                </b-button>

                                <recipe-products
                                    v-if="products.length"
                                    :products="products"
                                    :make-me-veggie-url="makeMeVeggieUrl"
                                />
                            </b-col>
                            <b-col
                                lg="6"
                                class="img-col"
                            >
                                <!-- desktop image -->
                                <lazy-image
                                    :src="desktopImage.src"
                                    :alt="desktopImage.alt"
                                    class="desktop-recipe-image"
                                    v-test:desktopImage
                                />

                                <b-button
                                    v-if="whereToBuyLink"
                                    variant="primary"
                                    class="where-to-buy-btn"
                                    :to="whereToBuyLink"
                                    id="whereToBuy"
                                    v-test:whereToBuyBtn
                                >
                                    where to buy?
                                </b-button>
                            </b-col>
                        </b-row>
                    </div>
                </b-container>
            </div>
        </section>
    </fade-up-animation>
</template>

<script>
    import RecipeDetails from '../RecipeDetails/RecipeDetails';
    import RecipeProducts from '../RecipeProducts/RecipeProducts';
    import ShareLinks from '../../molecules/ShareLinks/ShareLinks';
    import VueSetTimeout from '@netsells/vue-set-timeout';
    import itsuStoryBus from '../../../lib/itsu-story-bus';

    export default {
        name: 'recipe-header',

        components: { RecipeProducts, RecipeDetails, ShareLinks },

        mixins: [VueSetTimeout],

        props: {
            name: {
                type: String,
                required: true,
            },

            mobileImage: {
                type: Object,
                default: () => ({}),
            },

            desktopImage: {
                type: Object,
                required: true,
            },

            recipeDetails: {
                type: Object,
                required: true,
            },

            products: {
                type: Array,
                required: true,
            },

            makeMeVeggieUrl: {
                type: String,
                default: '',
            },

            whereToBuyLink: {
                type: String,
                default: '',
            },
        },

        methods: {
            /**
             * Scroll/focus to the instructions.
             */
            scrollToInstructions() {
                itsuStoryBus.$emit('scroll-to-instructions');

                const el = document.getElementById('recipe-details-section');

                if (!el) {
                    return;
                }

                this.setTimeout(() => {
                    el.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });

                    el.setAttribute('tabIndex', '-1');

                    el.focus({ preventScroll: true });
                }, 300);
            },
        },
    };
</script>

<style lang="scss" scoped>
    .image-holder {
        max-width: 484px;
        margin-left: auto;
        margin-right: auto;
    }

    .lazy-image {
        width: 100%;
        height: auto;
    }

    .mobile-recipe-image {
        @include media-breakpoint-up(lg) {
            display: none;
        }
    }

    .desktop-recipe-image {
        display: none;

        @include media-breakpoint-up(lg) {
            display: block;
            margin-bottom: 24px;
        }
    }

    .header-content-container {
        padding: 16px 24px;
    }

    .header {
        @include media-breakpoint-up(lg) {
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            border-bottom: solid 2px primary-palette(2);
            padding-bottom: 12px;
            margin-bottom: 24px;
        }
    }

    .name {
        font-size: rem(32px);
        font-weight: bold;
        margin-bottom: 12px;
        @include media-breakpoint-up(lg) {
            font-size: rem(48px);
            margin-bottom: 0;
        }
    }

    .share-links {
        margin-bottom: 20px;
        @include media-breakpoint-up(lg) {
            margin-left: 50px;
            margin-bottom: 0;
        }
    }

    .recipe-details {
        margin-bottom: 24px;

        @include media-breakpoint-up(lg) {
            padding-bottom: 24px;
            border-bottom: solid 2px primary-palette(2);
            margin-right: 40px;
        }
    }

    .start-cooking-btn {
        width: 100%;
        margin-bottom: 24px;

        @include media-breakpoint-up(lg) {
            display: none;
        }
    }

    .recipe-products {
        margin-bottom: 24px;
    }

    .where-to-buy-btn {
        @include media-breakpoint-between(xs, md) {
            width: 100%;
            background-color: #ffffff;
            border-color: primary-palette(1);
            color: primary-palette(1);

            &:hover {
                background-color: primary-palette(1);
                color: #ffffff;
            }
        }

        @include media-breakpoint-up(lg) {
            float: right;
            padding: 6px 30px;
        }
    }

    .img-col {
        @include media-breakpoint-only(lg) {
            padding-left: 25px;
        }
    }

    ::v-deep .link-container {
        @include media-breakpoint-only(lg) {
            margin-left: 0;
        }
    }
</style>
